import { useEffect, useRef, useState } from "react";
import { DealsAPI, type CreateDealDto } from "../../api/deals";
import { getProducts, type ProductDto } from "../../api/products";
import { UsersAPI } from "../../api/usersApi";
import type { ClientDto } from "../../api/clients";
import { ProductSelectV2 } from "../ProductSelectV2";
import { ClientSelectSmart } from "../ClientSelectSmart";
import CreateClientModal from "../clients/CreateClientModal";
import api from "../../api/axios";
import { TimelineAPI } from "../../api/timeline";
import "../../styles/create-deal.css";

const PAY_OPTIONS = [
  { value: "card",     label: "Карта",    icon: "💳", color: "#6366f1" },
  { value: "kaspi",    label: "Kaspi QR", icon: "🟡", color: "#f59e0b" },
  { value: "halyk",    label: "Halyk QR", icon: "🟢", color: "#10b981" },
  { value: "transfer", label: "Безнал",   icon: "🏦", color: "#60a5fa" },
  { value: "credit",   label: "Кредит",   icon: "🏷️", color: "#a78bfa" },
];

function formatDateReadable(date: Date) {
  const months = ["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

function getDailyDealNumber() {
  const today = new Date().toISOString().slice(0, 10);
  const savedDate = localStorage.getItem("dealCounterDate");
  let counter = Number(localStorage.getItem("dealCounterValue") ?? 0);
  if (savedDate !== today) {
    counter = 1;
    localStorage.setItem("dealCounterDate", today);
  } else {
    counter += 1;
  }
  localStorage.setItem("dealCounterValue", String(counter));
  return counter;
}

interface Props {
  pipelineId: number;
  stageId: number;
  clients: ClientDto[];
  users: any[];
  onCreated: () => void;
  onClose: () => void;
}

type Line = { product: ProductDto; quantity: number; price: number };

const fmtMoney = (n: number) => new Intl.NumberFormat("ru-RU").format(Math.round(n)) + " ₸";

/** Числовое поле, которое можно стереть и набрать заново: пока вводят, держит текст у себя, наружу отдаёт число. */
function NumField({ value, min, readOnly, className, onCommit }: {
  value: number; min: number; readOnly?: boolean; className?: string; onCommit: (v: number) => void;
}) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(String(value)); }, [value, focused]);
  return (
    <input
      className={`cdm-num ${className ?? ""}`}
      type="text" inputMode="numeric" pattern="[0-9]*" value={text} readOnly={readOnly}
      onFocus={(e) => { setFocused(true); e.target.select(); }}
      onChange={(e) => {
        const t = e.target.value.replace(/[^0-9]/g, "");
        setText(t);
        if (t !== "") onCommit(Math.max(min, Number(t)));
      }}
      onBlur={() => {
        setFocused(false);
        const n = text === "" ? min : Math.max(min, Number(text));
        setText(String(n));
        onCommit(n);
      }}
    />
  );
}

export const CreateDealModal = ({ pipelineId, stageId, clients, onCreated, onClose }: Props) => {
  const [clientId, setClientId]           = useState<number | null>(null);
  const [responsibleId, setResponsibleId] = useState<number | null>(null);
  const [title, setTitle]                 = useState("");
  const [products, setProducts]           = useState<Line[]>([]);
  const [allProducts, setAllProducts]     = useState<ProductDto[]>([]);
  const [employeeList, setEmployeeList]   = useState<any[]>([]);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [createClientPrefill, setCreateClientPrefill] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<string>("card");
  const [prepayment, setPrepayment]       = useState<string>("");
  const [dealDate, setDealDate]           = useState<string>(new Date().toISOString().slice(0, 10));
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState<string | null>(null);
  const [note, setNote]                   = useState("");
  const [creditBalance, setCreditBalance] = useState<any>(null);
  const downOnOverlay                     = useRef(false);

  useEffect(() => { getProducts().then(setAllProducts); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !showCreateClient) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, showCreateClient]);

  // Загружаем кредитный баланс при смене клиента
  useEffect(() => {
    if (!clientId) { setCreditBalance(null); return; }
    api.get(`/credit/balance/${clientId}`).then(r => setCreditBalance(r.data)).catch(() => setCreditBalance(null));
  }, [clientId]);

  // Если выбран кредит но клиент не подходит — сбрасываем
  useEffect(() => {
    if (paymentMethod === 'credit' && !creditAvailable) setPaymentMethod('card');
  }, [creditBalance, products]);

  useEffect(() => {
    UsersAPI.getAll().then(async (all) => {
      const me = await UsersAPI.me();
      const list = all.filter((u: any) => u.isActive);
      if (!list.find((u: any) => u.id === me.id)) list.push(me);
      setEmployeeList(list);
      setResponsibleId(me.id);
    });
  }, []);

  useEffect(() => {
    const d = dealDate ? new Date(dealDate + "T00:00:00") : new Date();
    setTitle(`Сделка — №${getDailyDealNumber()} от ${formatDateReadable(d)}`);
  }, [dealDate]);

  // Повторный выбор того же товара увеличивает количество, а не игнорируется
  const addProduct = (productId: number) => {
    const p = allProducts.find((x) => x.id === productId);
    if (!p) return;
    setProducts((prev) => {
      const i = prev.findIndex((x) => x.product.id === productId);
      if (i >= 0) return prev.map((x, j) => (j === i ? { ...x, quantity: x.quantity + 1 } : x));
      return [...prev, { product: p, quantity: 1, price: Number(p.salePrice) }];
    });
  };

  const setLine = (i: number, patch: Partial<Line>) =>
    setProducts((prev) => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const totalAmount = products.reduce((s, p) => s + p.price * p.quantity, 0);
  const prepayNum = Math.max(0, Number(prepayment) || 0);
  const prepayTooBig = totalAmount > 0 && prepayNum > totalAmount;
  const selectedClient = clients.find((c) => c.id === clientId);
  const itemsCount = products.reduce((s, p) => s + p.quantity, 0);

  const creditAvailable = (() => {
    const acc = creditBalance?.account;
    if (!acc || !acc.isActive) return false;
    if (creditBalance.overdueOrders?.length > 0) return false;
    if (!products.length) return false;
    if (acc.allowedProductIds?.length) {
      const allowed = acc.allowedProductIds.map(Number);
      if (!products.every((p) => allowed.includes(Number(p.product.id)))) return false;
    }
    if (acc.allowedCategoryIds?.length) {
      const allowed = acc.allowedCategoryIds.map(Number);
      if (!products.every((p) => allowed.includes(Number((p.product as any).categoryId ?? (p.product as any).category?.id)))) return false;
    }
    return true;
  })();

  const blocker = !clientId ? "Выберите клиента" : !responsibleId ? "Назначьте ответственного" : prepayTooBig ? "Предоплата больше суммы сделки" : null;

  const createDeal = async () => {
    if (blocker || !clientId || !responsibleId) { setError(blocker); return; }
    setError(null);
    setSaving(true);
    try {
      const dto: CreateDealDto = { title, clientId, pipelineId, stageId };
      const res = await DealsAPI.create(dto);
      const dealId = res.data.id;
      for (const p of products) {
        await DealsAPI.addProduct(dealId, { productId: p.product.id, quantity: p.quantity, price: p.price });
      }
      await DealsAPI.update(dealId, { responsibleUserId: responsibleId, paymentMethod, prepayment: prepayNum, dealDate });
      if (note.trim()) {
        try { await TimelineAPI.add({ dealId, type: "comment", message: note.trim() }); } catch { /* заметка не критична */ }
      }
      if (paymentMethod === 'credit') {
        try {
          await api.post(`/credit/orders/from-deal/${dealId}`);
        } catch (e: any) {
          alert(e?.response?.data?.message ?? 'Сделка создана, но кредитный заказ не оформлен');
        }
      }
      onCreated();
      onClose();
    } catch (e: any) {
      const m = e?.response?.data?.message;
      setError(Array.isArray(m) ? m.join(", ") : m || "Не удалось создать сделку");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="cdm-overlay"
      onMouseDown={(e) => { downOnOverlay.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (downOnOverlay.current && e.target === e.currentTarget) onClose(); }}
    >
      <div className="create-deal-modal cdm" role="dialog" aria-modal="true">

        <div className="cdm-head">
          <div className="cdm-head-top">
            <span className="cdm-pill">Новая сделка</span>
            {totalAmount > 0 && <span className="cdm-pill sum">{fmtMoney(totalAmount)}</span>}
            <div style={{ flex: 1 }} />
            <button type="button" className="cdm-close" onClick={onClose} aria-label="Закрыть">✕</button>
          </div>
          <input className="cdm-title" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Название сделки" />
        </div>

        <div className="create-deal-body cdm-body">

          {/* ЛЕВАЯ КОЛОНКА — что продаём и кому */}
          <div className="create-deal-left cdm-main">

            <section className="cdm-card">
              <h4>Клиент</h4>
              <ClientSelectSmart clients={clients} value={clientId}
                onSelect={(c) => setClientId(c.id)}
                onCreateNew={(p) => { setCreateClientPrefill(p); setShowCreateClient(true); }} />
              {selectedClient && (
                <div className="cdm-client">
                  <div className="cdm-avatar">{(selectedClient.firstName?.[0] ?? "?").toUpperCase()}</div>
                  <div style={{ minWidth: 0 }}>
                    <div className="cdm-client-name">{selectedClient.firstName} {selectedClient.lastName}</div>
                    <div className="cdm-client-sub">{[selectedClient.phone, selectedClient.email].filter(Boolean).join(" · ")}</div>
                  </div>
                </div>
              )}
              <div className="cdm-row2">
                <label className="cdm-field">
                  <span>Ответственный</span>
                  <select value={responsibleId ?? ""} onChange={(e) => setResponsibleId(Number(e.target.value))}>
                    {employeeList.map((u) => <option key={u.id} value={u.id}>{u.lastName} {u.firstName}</option>)}
                  </select>
                </label>
                <label className="cdm-field">
                  <span>Дата сделки</span>
                  <input type="date" value={dealDate} onChange={(e) => setDealDate(e.target.value)} />
                </label>
              </div>
            </section>

            <section className="cdm-card">
              <h4>Товары и услуги {itemsCount > 0 && <span className="cdm-count">{itemsCount}</span>}</h4>
              <ProductSelectV2 products={allProducts} onSelect={(p) => addProduct(p.id)} />

              {products.length === 0 ? (
                <div className="cdm-empty">Позиции можно добавить позже. Сделку допустимо создать пустой, например по звонку.</div>
              ) : (
                <div className="cdm-lines">
                  {products.map((p, i) => {
                    const stock = p.product.stock != null ? Number(p.product.stock) : null;
                    const low = stock != null && stock < p.quantity;
                    return (
                      <div key={p.product.id} className="cdm-line deal-prod-row">
                        <div className="cdm-line-top">
                          <div className="cdm-line-name deal-prod-name">{p.product.nameRu}</div>
                          <button type="button" className="cdm-del" onClick={() => setProducts((prev) => prev.filter((_, j) => j !== i))} aria-label="Убрать позицию">✕</button>
                        </div>
                        <div className="cdm-line-ctrl">
                          <div className="cdm-step">
                            <button type="button" onClick={() => setLine(i, { quantity: Math.max(1, p.quantity - 1) })} aria-label="Меньше">−</button>
                            <NumField value={p.quantity} min={1} onCommit={(v) => setLine(i, { quantity: v })} />
                            <button type="button" onClick={() => setLine(i, { quantity: p.quantity + 1 })} aria-label="Больше">+</button>
                          </div>
                          <span className="cdm-mul">×</span>
                          <div className="cdm-price">
                            <NumField value={p.price} min={0} readOnly={p.product.type !== "service"} className="price"
                              onCommit={(v) => setLine(i, { price: v })} />
                            <span>₸</span>
                          </div>
                          {stock != null && <span className={`cdm-stock ${low ? "low" : ""}`} title="Остаток на складе">ост. {stock}</span>}
                          <div className="cdm-line-total">{fmtMoney(p.price * p.quantity)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          {/* ПРАВАЯ КОЛОНКА — оплата и заметка */}
          <div className="create-deal-right cdm-side">

            <section className="cdm-card">
              <h4>Оплата</h4>
              <div className="pay-options-grid cdm-pay">
                {PAY_OPTIONS.filter(p => p.value !== 'credit' || creditAvailable).map((p) => (
                  <button key={p.value} type="button" onClick={() => setPaymentMethod(p.value)}
                    className={`cdm-chip ${paymentMethod === p.value ? "on" : ""}`}
                    style={paymentMethod === p.value ? { borderColor: p.color, background: `${p.color}22`, color: p.color } : undefined}>
                    <span>{p.icon}</span> {p.label}
                  </button>
                ))}
              </div>

              <div className="cdm-field" style={{ marginTop: 12 }}>
                <span>Предоплата, ₸</span>
                {totalAmount > 0 && (
                  <div className="cdm-quick">
                    {[0, 30, 50, 100].map((pc) => (
                      <button key={pc} type="button" onClick={() => setPrepayment(pc ? String(Math.round(totalAmount * pc / 100)) : "")}>
                        {pc ? `${pc}%` : "Нет"}
                      </button>
                    ))}
                  </div>
                )}
                <input type="text" inputMode="numeric" placeholder="0" value={prepayment}
                  onChange={(e) => setPrepayment(e.target.value.replace(/[^0-9]/g, ""))} />
              </div>

              <div className="cdm-sum">
                <div><span>Позиций</span><b>{itemsCount}</b></div>
                <div><span>Сумма</span><b>{fmtMoney(totalAmount)}</b></div>
                {prepayNum > 0 && <div><span>Предоплата</span><b>{fmtMoney(prepayNum)}</b></div>}
                {prepayNum > 0 && totalAmount > 0 && (
                  <div className={prepayTooBig ? "bad" : "ok"}><span>Остаток к оплате</span><b>{fmtMoney(totalAmount - prepayNum)}</b></div>
                )}
              </div>
            </section>

            <section className="cdm-card cdm-note">
              <h4>Заметка к сделке</h4>
              <textarea rows={4} placeholder="Контекст, пожелания клиента, о чём договорились. Появится в таймлайне."
                value={note} onChange={(e) => setNote(e.target.value)} />
            </section>
          </div>
        </div>

        <div className="create-deal-footer cdm-foot">
          <div className="cdm-foot-total">
            <span>Итого</span>
            <b>{fmtMoney(totalAmount)}</b>
          </div>
          {(error || blocker) && <div className="cdm-foot-msg" role="alert">{error ?? blocker}</div>}
          <button type="button" className="cdm-btn sec" onClick={onClose}>Отмена</button>
          <button type="button" className="cdm-btn pri" onClick={createDeal} disabled={!!blocker || saving}>
            {saving ? "Создание…" : "Создать сделку"}
          </button>
        </div>

        {showCreateClient && (
          <CreateClientModal prefill={createClientPrefill}
            onClose={() => setShowCreateClient(false)}
            onCreated={(c) => { if (!clients.some((x) => x.id === c.id)) clients.push(c); setClientId(c.id); setShowCreateClient(false); }} />
        )}
      </div>
    </div>
  );
};
